import { OrderResponseV2 } from '@portal/state/internal/getOrderDetails/types';
import type { AfterShipTracking } from 'api/aftership/types';
import { routes } from 'constants/routes';
import { differenceInDays, fromUnixTime } from 'date-fns';
import { UPCOMING_ORDER_STATUS, type UpcomingOrderStatus } from 'shared/constants/orders';
import { useGetPrioritizedUpcomingOrderV2 } from 'shared/hooks/useGetPrioritizedUpcomingOrderV2';
import { extractTrackingNumberFromUrl, getUpcomingOrderStatus } from 'shared/utils/afterShip';
import { getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import { useAfterShipTracking } from 'state/hooks/useAfterShipTracking';
import { useGetNextSubscriptionOrderV2 } from 'state/hooks/useGetNextSubscriptionOrderV2';
import type { SubscriptionDetailsV2Response } from 'state/internal/getSubscriptionDetails/types';

/**
 * Constants for order tracking
 */
export const ORDER_AGE_LIMIT_IN_DAYS = 10;
export const DELIVERED_AGE_LIMIT_IN_DAYS = 3;

/**
 * Type definition for an upcoming order
 * Used by components that display information about upcoming orders
 */
export type UpcomingOrder = {
  /** Whether this is the next order or current order */
  isNextOrder: boolean;
  /** Current status of the order (e.g., "Preparing", "Shipped", "Delivered") */
  status: UpcomingOrderStatus | '';
  /** Timestamp for the order */
  date: Date | null;
  /** URL path for viewing order details or tracking the shipment */
  path: string;
};

/**
 * Checks if a delivered order is still recent enough to show tracking information
 * Orders delivered within the last DELIVERED_AGE_LIMIT_IN_DAYS days are considered recent
 */
const isRecentlyDelivered = (deliveryDate: Date): boolean => {
  const daysSinceDelivery = differenceInDays(new Date(), deliveryDate);
  return daysSinceDelivery < DELIVERED_AGE_LIMIT_IN_DAYS;
};

/**
 * Constructs an UpcomingOrder object from an existing order
 */
const constructUpcomingOrderFromExistingOrder = (
  order: OrderResponseV2,
  afterShipTracking: AfterShipTracking | null
): UpcomingOrder | null => {
  const orderTimestamp = Number(order.order_date);

  if (Number.isNaN(orderTimestamp)) {
    return null;
  }

  const date = fromUnixTime(orderTimestamp);

  const status = afterShipTracking
    ? getUpcomingOrderStatus(afterShipTracking.tag)
    : UPCOMING_ORDER_STATUS.PREPARING;

  // If the order is delivered but not recently, return null
  if (status === UPCOMING_ORDER_STATUS.DELIVERED && !isRecentlyDelivered(date)) {
    return null;
  }

  return {
    isNextOrder: false,
    status,
    date,
    path:
      order.tracking_url ||
      routes.orderDetail({ id: getIdFromShopifyFormattedId(order.shopify_order_id) })
  };
};

/**
 * Constructs an UpcomingOrder object from a subscription
 */
const constructUpcomingOrderFromSubscription = (
  nextSubscriptionOrder: SubscriptionDetailsV2Response
): UpcomingOrder | null => {
  const { next_order_date: nextOrderDate, shopify_contract_id: contractId } = nextSubscriptionOrder;
  const subscriptionId = getIdFromShopifyFormattedId(contractId) as string;

  const orderTimestamp = Number(nextOrderDate);

  if (Number.isNaN(orderTimestamp)) {
    return null;
  }

  const date = fromUnixTime(orderTimestamp);
  const path = routes.subscriptionDetail({ id: subscriptionId });

  return {
    isNextOrder: true,
    status: '',
    date,
    path
  };
};

/**
 * Hook to get upcoming order information
 *
 * @param subscriptionId Optional subscription ID to filter orders
 * @returns Information about the upcoming order, or null if none found
 */
export const useUpcomingOrder = (subscriptionId?: string): UpcomingOrder | null => {
  const { nextSubscriptionOrder } = useGetNextSubscriptionOrderV2(subscriptionId);
  const { shopify_contract_id: nextSubscriptionId } = nextSubscriptionOrder || {};

  // Only retrieve upcoming orders within last 10 days
  const prioritizedUpcomingOrder = useGetPrioritizedUpcomingOrderV2(
    ORDER_AGE_LIMIT_IN_DAYS,
    subscriptionId || (nextSubscriptionId && getIdFromShopifyFormattedId(nextSubscriptionId))
  );

  const prioritizedOrderTrackingUrl = prioritizedUpcomingOrder?.tracking_url;
  const prioritizedOrderTrackingNumber =
    prioritizedOrderTrackingUrl && extractTrackingNumberFromUrl(prioritizedOrderTrackingUrl);

  const { data: afterShipTracking } = useAfterShipTracking(prioritizedOrderTrackingNumber ?? '');

  if (prioritizedUpcomingOrder) {
    return constructUpcomingOrderFromExistingOrder(
      prioritizedUpcomingOrder,
      afterShipTracking ?? null
    );
  }

  if (nextSubscriptionOrder) {
    return constructUpcomingOrderFromSubscription(nextSubscriptionOrder);
  }

  return null;
};
